import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Image from "next/image";
import Link from "next/link";
import {
  BanknoteArrowUpIcon,
  BookmarkIcon,
  BookOpenIcon,
  ChartColumnIncreasingIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EqualIcon,
  HeadsetIcon,
  LaptopMinimalCheckIcon,
  LinkIcon,
  LuggageIcon,
  MailCheckIcon,
  MailSearchIcon,
  RadioIcon,
  SmilePlusIcon,
  UserCheckIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useScrolled } from "@/hooks/use-scrolled";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const menuOptions = {
  products: {
    label: "Products",
    options: [
      {
        title: "Leads",
        subTitle:
          "Instant access to 626M+ triple-verified leads ready for cold outreach.",
        icon: UserCheckIcon,
        link: "/triple-verified-leads",
      },
      {
        title: "Intent Data",
        subTitle:
          "Reach buyers searching for your solution with 21B+ real-time intent signals.",
        icon: RadioIcon,
        link: "/unlimited-intent-data",
      },
      {
        title: "Email Sending",
        subTitle:
          "Send emails that land and convert, using warm inboxes and smart automation.",
        icon: MailCheckIcon,
        link: "/cold-email-setup",
      },
      // {
      //   title: "Domain Setup",
      //   subTitle:
      //     "Launch new domains with perfect deliverability and zero setup hassle.",
      //   icon: GlobeIcon,
      //   link: "/",
      // },
      // {
      //   title: "Website Visitors",
      //   subTitle:
      //     "See exactly who visits your site and turn anonymous traffic into leads.",
      //   icon: EyeIcon,
      //   link: "/",
      // },
      // {
      //   title: "AI Agents",
      //   subTitle:
      //     "Deploy AI-powered reps that qualify leads and book meetings for you.",
      //   icon: BotMessageSquareIcon,
      //   link: "/",
      // },
    ],
  },
  resources: {
    label: "Resources",
    options: [
      {
        title: "Blog",
        subTitle:
          "Get expert tips, strategies, and insights on cold email marketing.",
        icon: BookmarkIcon,
        link: "/blog",
      },
      {
        title: "Case Studies",
        subTitle:
          "See how businesses have scaled with ListKit's proven approach.",
        icon: ChartColumnIncreasingIcon,
        link: "/case-studies",
      },
      {
        title: "Testimonials",
        subTitle:
          "Read success stories from users who’ve transformed their outreach with ListKit.",
        icon: SmilePlusIcon,
        link: "/wall-of-love",
      },
      {
        title: "Cold Email Setup",
        subTitle:
          "Quickly launch your cold email campaigns with our easy setup guide.",
        icon: MailSearchIcon,
        link: "/cold-email-setup",
      },
      {
        title: "Affiliate Program",
        subTitle: "Earn commissions by referring new users to ListKit.",
        icon: BanknoteArrowUpIcon,
        link: "/affiliates",
      },
      {
        title: "Lead Magnet",
        subTitle:
          "Access free tools and templates to improve your lead generation.",
        icon: BookOpenIcon,
        link: "/opt-in-for-lead-magnet",
      },
      {
        title: "HubSpot Integration",
        subTitle:
          "Easily connect ListKit with your CRM, including HubSpot, Salesforce, and more.",
        icon: LinkIcon,
        link: "/hubspot-integration",
      },
      {
        title: "Demo",
        subTitle:
          "Experience ListKit live and see how it works for your business.",
        icon: LaptopMinimalCheckIcon,
        link: "/demo",
      },
      {
        title: "Strategy Call",
        subTitle:
          "Book a 1-on-1 call for expert advice on optimizing your cold outreach.",
        icon: HeadsetIcon,
        link: "/strategy-call",
      },
    ],
  },
  company: {
    label: "Company",
    options: [
      {
        title: "Careers",
        subTitle:
          "Join a growing team that’s revolutionizing the way businesses generate leads.",
        icon: LuggageIcon,
        link: "/job",
      },
      {
        title: "Team",
        subTitle:
          "Meet the innovators behind ListKit, passionate about helping you grow.",
        icon: UsersIcon,
        link: "/about",
      },
    ],
  },
};

export default function TopNav() {
  const [openMenu, setOpenMenu] = useState<keyof typeof menuOptions | null>(
    null
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openCollapsible, setOpenCollapsible] = useState<
    keyof typeof menuOptions | null
  >(null);

  const scrolled = useScrolled(10);

  return (
    <nav
      className={cn(
        "!fixed !overflow-y-auto !max-h-vh w-full z-50 lg:z-40",
        mobileMenuOpen && "inset-0"
      )}
    >
      <div
        className={cn(
          "w-full flex items-center justify-center",
          scrolled && "bg-white border-b border-[#0D2A4C08]"
        )}
      >
        <div className="w-full max-w-[1120px] flex justify-between items-center p-4">
          <div className="items-center gap-[22px] flex">
            <div className="w-[92px] lg:w-[110px]">
              <Link href="/">
                <Image
                  src="/images/listkit-logo.webp"
                  alt="Listkit Logo"
                  width={415}
                  height={120}
                  className="w-full"
                />
              </Link>
            </div>
            <div className="items-center gap-[18px] hidden lg:flex">
              {Object.entries(menuOptions).map(([key, entry], i) => (
                <Popover
                  key={`${key}-${i}`}
                  open={openMenu === key}
                  onOpenChange={(isOpen) =>
                    setOpenMenu(
                      isOpen ? (key as keyof typeof menuOptions) : null
                    )
                  }
                >
                  <PopoverTrigger asChild>
                    <button
                      className={cn(
                        "text-[#0D2A4C99] flex items-center !text-[16px] gap-1 hover:text-[#0c8ce9] font-medium cursor-pointer",
                        openMenu === key && "!text-black !font-bold"
                      )}
                    >
                      <p>{entry.label}</p>
                      {openMenu === key ? (
                        <ChevronUpIcon className="w-4" />
                      ) : (
                        <ChevronDownIcon className="w-4" />
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={25}
                    className={cn(
                      "grid grid-cols-2 p-6 gap-8 rounded-[12px] z-50 bg-white w-fit",
                      openMenu === "company" && "grid-cols-1"
                    )}
                  >
                    {entry.options?.map((option, optIndex) => (
                      <Link
                        href={option.link}
                        key={`${option.title}-${optIndex}`}
                        className="max-w-[360px] rounded-[12px] cursor-pointer flex items-center gap-[17px] group"
                      >
                        <div className="flex justify-center items-center size-[66px] bg-[#0D2A4C08] border border-[#0D2A4C08] rounded-[8px]">
                          <option.icon className="size-[40px] text-[#0c8ce9]" />
                        </div>
                        <div className="flex flex-col flex-1 gap-1.5 items-start">
                          <p className="text-[#0D2A4C] text-xl font-semibold group-hover:text-[#0c8ce9]">
                            {option.title}
                          </p>
                          <p className="text-[#0D2A4C99] text-sm font-medium text-start">
                            {option.subTitle}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </PopoverContent>
                </Popover>
              ))}
              <Link
                href="pricing"
                className="text-[#0D2A4C99] flex items-center !text-[16px] gap-1 font-medium hover:text-[#0c8ce9]"
              >
                Pricing
              </Link>
              {openMenu !== null && (
                <div className="fixed inset-0 z-40 bg-[#00000008]" />
              )}
            </div>
          </div>
          <div className="hidden lg:flex gap-[18px] items-center">
            <Link
              href="https://app.listkit.io/login"
              className="text-[#0D2A4C] font-semibold text-[16px] cursor-pointer hover:text-[#0c8ce9]"
            >
              Log In
            </Link>
            <Link
              href="/pricing"
              className="px-[18px] py-3 text-[#0D2A4C] font-semibold text-[16px] bg-[#F8F9FA] hover:bg-white hover:text-[#0c8ce9] active:bg-[#F8F9FA] border border-[#0D2A4C08] rounded-[100px] cursor-pointer"
            >
              Create Your Account
            </Link>
          </div>
          <div className="block lg:hidden">
            <button
              aria-label="Mobile Menu Open Close"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
            >
              {!mobileMenuOpen && <EqualIcon className="size-8" />}
              {mobileMenuOpen && <XIcon className="size-8" />}
            </button>
          </div>
        </div>
      </div>
      {mobileMenuOpen && (
        <>
          <div className="w-full flex lg:hidden flex-col bg-white z-50 overflow-auto">
            <Link
              href="pricing"
              className="py-[22px] px-[19px] borber-b border-[#0D2A4C08] w-full cursor-pointer"
            >
              <p className="text-xl font-semibold text-[#0D2A4C]">Pricing</p>
            </Link>
            {Object.entries(menuOptions).map(([key, entry], i) => (
              <Collapsible
                key={`${key}-${i}`}
                open={openCollapsible === key}
                onOpenChange={(isOpen) =>
                  setOpenCollapsible(
                    isOpen ? (key as keyof typeof menuOptions) : null
                  )
                }
              >
                <CollapsibleTrigger asChild>
                  <button
                    className={cn(
                      "py-[22px] px-[19px] borber-b border-[#0D2A4C08] w-full cursor-pointer flex items-center justify-between"
                    )}
                  >
                    <p className="text-xl font-semibold text-[#0D2A4C]">
                      {entry.label}
                    </p>
                    {openCollapsible === key ? (
                      <ChevronUpIcon className="w-8" />
                    ) : (
                      <ChevronDownIcon className="w-8" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent
                  className={cn(
                    "grid grid-cols-1 p-6 gap-8 rounded-[12px] z-50 bg-white w-fit"
                  )}
                >
                  {entry.options?.map((option, optIndex) => (
                    <Link
                      href={option.link}
                      key={`${option.title}-${optIndex}`}
                      className="max-w-[360px] rounded-[12px] cursor-pointer flex items-center gap-[17px] group"
                    >
                      <div className="flex justify-center items-center size-[66px] bg-[#0D2A4C08] border border-[#0D2A4C08] rounded-[8px]">
                        <option.icon className="size-[40px] text-[#0c8ce9]" />
                      </div>
                      <div className="flex flex-col flex-1 gap-1.5 items-start">
                        <p className="text-[#0D2A4C] text-xl font-semibold group-hover:text-[#0c8ce9]">
                          {option.title}
                        </p>
                        <p className="text-[#0D2A4C99] text-sm font-medium text-start">
                          {option.subTitle}
                        </p>
                      </div>
                    </Link>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))}
            <div className="flex flex-col gap-3.5 py-6 px-5 items-center justify-center">
              <Link
                href="https://app.listkit.io/login"
                className="bg-[#F8F9FA] text-center border hover:bg-white active:outline-1 active:outline-[#F8F9FA] active:bg-[#F8F9FA] border-[#0D2A4C08] rounded-[100px] py-[18px] px-[32px] w-full cursor-pointer"
              >
                <p className="text-lg font-semibold text-[#0D2A4C]">Log In</p>
              </Link>
              <Link
                href="/pricing"
                className="bg-[#2A8DFF] text-center hover:bg-[#2A8DFF]/90 active:outline-1 active:outline-[#2A8DFF] active:bg-[#2A8DFF] rounded-[100px] py-[18px] px-[32px] w-full cursor-pointer"
              >
                <p className="text-lg font-semibold text-white">
                  Get Started Now
                </p>
              </Link>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
